import { Box } from '@mui/material';
import { Fragment } from 'react';
import { useRecoilValue } from 'recoil';
import DNDArea from '~/components/DicomLoad/LocalLoad/DNDArea';
import FilesProcessed from '~/components/DicomLoad/LocalLoad/FilesProcessed';
import OperationsToolbar from '~/components/toolbar/OperationsToolbar/OperationsToolbar';
import StructureColorPopover from '~/components/Structures/StructureColorPopover/StructureColorPopover';
import DicomSourcesModal from '~/containers/patients-modal/DicomSourcesModal';
import GridViewport from '~/containers/viewer/GridViewport';
import ExportDatasetModal from '~/containers/viewer/explorer-sidebar/Export/ExportDatasetModal';
import BackendWorkflows from '~/containers/workflows/BackendWorkflows';
import ConnectorWorkflows from '~/containers/workflows/ConnectorWorkflows';
import { useDoseStepDisplayPolicy } from '~/hooks/dose/useDoseStepDisplayPolicy';
import { usePetSuvAvailabilityWarning } from '~/hooks/image/usePetSuvAvailabilityWarning';
import ViewerOpenLoader from '~/hooks/openInViewer/ViewerOpenLoader';
import { useRegistrationModeShiftToggle } from '~/hooks/registration/useRegistrationModeShiftToggle';
import { useActiveStructurePeek } from '~/hooks/viewer/useActiveStructurePeek';
import { useActiveStructurePeekShortcut } from '~/hooks/viewer/useActiveStructurePeekShortcut';
import { useAdjacentSliceGhosts } from '~/hooks/viewer/useAdjacentSliceGhosts';
import { useComparisonRoiIsolation } from '~/hooks/viewer/useComparisonRoiIsolation';
import { useContourComparison } from '~/hooks/viewer/useContourComparison';
import { useDoseDisplayBridge } from '~/hooks/viewer/useDoseDisplayBridge';
import { useLineEraseToggleShortcut } from '~/hooks/viewer/useLineEraseToggleShortcut';
import { useReferenceDiff } from '~/hooks/viewer/useReferenceDiff';
import { useResetDrawingConstraintsOnSetChange } from '~/hooks/viewer/useResetDrawingConstraintsOnSetChange';
import { useStructureIsolation } from '~/hooks/viewer/useStructureIsolation';
import { useStartEngine } from '~/hooks/viewer/useStartEngine';
import { useStructureSelectionBridge } from '~/hooks/viewer/useStructureSelectionBridge';
import { useZoomToCursor } from '~/hooks/viewer/useZoomToCursor';
import { useBackendJobOrchestrator } from '~/hooks/workflows/backend/useBackendJobOrchestrator';
import { useConnectorJobOrchestrator } from '~/hooks/workflows/connector/useConnectorJobOrchestrator';
import { sessionDataAtom } from '~/state/atoms/sessionAtoms';
import AppConfig from '~/utils/appconfig';
import { useReferenceRowTie } from '~/hooks/viewer/useReferenceRowTie';
import { useReferenceRowLifecycle } from '~/hooks/viewer/useReferenceRowLifecycle';
import { useDeformedPreview } from '~/hooks/viewer/useDeformedPreview';

/**
 * Runs the backend job orchestrator for standalone (non-connector) mode.
 * Extracted to its own component so it can be conditionally rendered.
 */
function BackendJobOrchestratorProvider() {
    useBackendJobOrchestrator();
    return null;
}

/**
 * Runs the connector job orchestrator when connector mode is active
 * outside a pre-established session (SessionLayout owns the orchestrator
 * for the session-scoped entry point).
 */
function ConnectorJobOrchestratorProvider() {
    useConnectorJobOrchestrator();
    return null;
}

export default function Viewer() {
    const sessionData = useRecoilValue(sessionDataAtom);
    const isConnectorMode = AppConfig.isConnectorMode;

    useStartEngine();
    useResetDrawingConstraintsOnSetChange();
    // Shift swaps the registration mode of the armed registration tool.
    useRegistrationModeShiftToggle();
    // E flips the line tool between draw and erase.
    useLineEraseToggleShortcut();
    // V peeks under the active structure for as long as it is held.
    useActiveStructurePeekShortcut();
    // The selection, isolate, peek, adjacent-slice ghosts, difference
    // overlay, contour comparison and its ROI isolation, dose display,
    // zoom-to-cursor preference and the SUV warning run for the life of the
    // viewer, independent of the tiles.
    useStructureSelectionBridge();
    useStructureIsolation();
    useActiveStructurePeek();
    useAdjacentSliceGhosts();
    useReferenceDiff();
    useContourComparison();
    useComparisonRoiIsolation();
    useDoseStepDisplayPolicy();
    useDoseDisplayBridge();
    useZoomToCursor();
    usePetSuvAvailabilityWarning();
    useReferenceRowTie();
    useReferenceRowLifecycle();
    useDeformedPreview();

    return (
        <Fragment>
            {!isConnectorMode && <BackendJobOrchestratorProvider />}
            {isConnectorMode && !sessionData && (
                <ConnectorJobOrchestratorProvider />
            )}
            {/* A grid item may grow to its content's width unless told
                otherwise; the viewer must shrink to the area it is given. */}
            <Box
                position="relative"
                width="100%"
                height="100%"
                sx={{ userSelect: 'none', minWidth: 0 }}
            >
                <GridViewport />
                {/* Takes input only with no patient loaded, or while files
                    are dragged in from the desktop. */}
                <DNDArea />
                <OperationsToolbar />
            </Box>
            {/* Portal-rendered over the viewer. */}
            {!sessionData && <DicomSourcesModal />}
            <ExportDatasetModal />
            {/* The one colour popover; every colour edit opens in it. */}
            <StructureColorPopover />
            {isConnectorMode ? <ConnectorWorkflows /> : <BackendWorkflows />}
            <ViewerOpenLoader />
            {!sessionData && <FilesProcessed />}
        </Fragment>
    );
}
